import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { environment } from '../../../../../environments/environment';
import { BannerComponent } from '../../../../shared/components/banner/banner.component';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { selectCurrentUser } from '../../../../store/auth/auth.selectors';
import { selectPassportMenu } from '../../../../store/settings/settings.selectors';
import { User } from '../../../../core/interfaces/user.interface';
import { Country } from '../../../../core/interfaces/doctor.interface';
import { DashboardService } from '../../../../core/services/dashboard.service';
import { FrNumberPipe } from '../../../../shared/pipes/fr-number.pipe';
import { ModalService } from '../../../../core/services/modal.service';
import { ToastService } from '../../../../core/services/toast.service';
import { DocumentService } from '../../../../core/services/document.service';
import { DocumentViewerModalComponent } from '../../../../shared/components/modal/document-viewer-modal';
import { LucideAngularModule, FileText } from 'lucide-angular';
import { UserStats } from '../../../../core/interfaces/user-stats.interface';

interface MenuItem {
  title: string;
  label: string;
  image: string;
  navigate: () => void;
  order: number;
}

@Component({
  selector: 'app-passport-dashboard',
  standalone: true,
  imports: [CommonModule, BannerComponent, FrNumberPipe, LucideAngularModule],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class PassportDashboardComponent implements OnInit, OnDestroy {
  isDppTheme = environment.theme.default === 'dpp';
  links = [{ label: 'Votre passeport', href: undefined }];
  userData$!: Observable<User | null>;
  selectedCountry: Country | null = null;
  // DocumentsComponent-like state
  currentUser: User | null = null;
  selectedPassport: string = '';
  statistics$?: Observable<any>;
  importantDocuments$?: Observable<any>;
  visibleMenuItems$!: Observable<MenuItem[]>;
  private subscription = new Subscription();
  amountToBeReimbursed: number = 0;
  readonly FileTextIcon = FileText;

  // Configuration des menus disponibles
  private readonly menuConfig: Record<string, Omit<MenuItem, 'order'>> = {
    APPOINTMENTS: {
      title: 'APPOINTMENTS',
      label: 'Votre Suivi Santé',
      image: 'images/calendar.jpg',
      navigate: () => this.navigateToHealthTracking(),
    },
    PATIENT_RECORD: {
      title: 'PATIENT_RECORD',
      label: 'Votre dossier patient',
      image: 'images/docs.jpg',
      navigate: () => this.navigateToPatientRecord(),
    },
    INSURANCES: {
      title: 'INSURANCES',
      label: 'Vos contrats',
      image: 'images/family.jpg',
      navigate: () => this.navigateToInsurances(),
    },
    AUTHORIZATIONS: {
      title: 'AUTHORIZATIONS',
      label: 'Vos autorisations',
      image: 'images/padlock.jpg',
      navigate: () => this.navigateToAuthorizations(),
    },
    PAYMENTS: {
      title: 'PAYMENTS',
      label: 'Vos paiements',
      image: 'images/51264.jpg',
      navigate: () => this.navigateToPayments(),
    },
    // CARD: {
    //   title: 'CARD',
    //   label: 'Votre carte',
    //   image: 'images/4037.jpg',
    //   navigate: () => this.navigateToCard(),
    // }
  };

  constructor(
    private router: Router,
    private store: Store,
    private dashboardService: DashboardService,
    private modalService: ModalService,
    private toastService: ToastService,
    private documentService: DocumentService
  ) {
    this.userData$ = this.store.select(selectCurrentUser);

    // Créer un observable qui combine les settings et génère les menus visibles
    this.visibleMenuItems$ = this.store.select(selectPassportMenu).pipe(
      map((passportMenu) => {
        if (!passportMenu || passportMenu.length === 0) {
          // Si pas de settings, retourner tous les menus par défaut
          return Object.values(this.menuConfig).map((config) => ({
            ...config,
            order: 999,
          }));
        }

        // Filtrer et mapper les menus selon les settings
        return passportMenu
          .filter((menu) => menu.isVisible)
          .map((menu) => {
            const config = this.menuConfig[menu.title];
            if (config) {
              return {
                ...config,
                order: menu.order,
              };
            }
            return null;
          })
          .filter((item): item is MenuItem => item !== null)
          .sort((a, b) => a.order - b.order);
      })
    );
  }

  ngOnInit(): void {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('defaultCountry');
      if (saved) {
        try {
          this.selectedCountry = JSON.parse(saved);
        } catch {
          this.selectedCountry = null;
        }
      }
    }

    // Surveiller l'utilisateur courant (pattern from DocumentsComponent)
    this.subscription.add(
      this.store.select(selectCurrentUser).subscribe((currentUser) => {
        this.currentUser = currentUser;
        this.selectedPassport = currentUser?.passportNumber || '';

        // Appeler l'API de statistiques quand on a le passeport
        if (this.selectedPassport) {
          this.statistics$ = this.dashboardService.getStatistics(
            this.selectedPassport
          );

          // Charger les user-stats et affecter amountToReimburse.total à amountToBeReimbursed
          this.subscription.add(
            this.dashboardService.getUserStats().subscribe({
              next: (stats) => {
                this.amountToBeReimbursed = stats.amountToReimburse?.total ?? 0;
              },
              error: (error) => {
                console.error(
                  'PassportDashboardComponent: Erreur lors de la récupération des user-stats:',
                  error
                );
              },
            })
          );

          // S'abonner pour déclencher la requête
          this.subscription.add(
            this.statistics$.subscribe({
              next: (statistics) => {
                // console.log('PassportDashboardComponent: Statistiques récupérées:', statistics);
              },
              error: (error) => {
                console.error(
                  'PassportDashboardComponent: Erreur lors de la récupération des statistiques:',
                  error
                );
              },
            })
          );

          // Charger les documents importants (favoris)
          this.importantDocuments$ =
            this.dashboardService.getImportantDocuments(
              this.selectedPassport,
              1,
              10
            );

          this.subscription.add(
            this.importantDocuments$.subscribe({
              next: (docs) => {
                console.log(
                  'PassportDashboardComponent: Documents importants récupérés:',
                  docs
                );
              },
              error: (error) => {
                console.error(
                  'PassportDashboardComponent: Erreur lors de la récupération des documents importants:',
                  error
                );
              },
            })
          );
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  navigateToAppointments(): void {
    this.router.navigate([
      `/${this.selectedCountry?.code}/passport/appointments`,
    ]);
  }

  navigateToCard(): void {
    this.router.navigate([`/${this.selectedCountry?.code}/passport/card`]);
  }

  navigateToInsurances(): void {
    this.router.navigate([
      `/${this.selectedCountry?.code}/passport/insurances`,
    ]);
  }

  navigateToPatientRecord(): void {
    this.router.navigate([
      `/${this.selectedCountry?.code}/passport/patient-record`,
    ]);
  }

  navigateToAuthorizations(): void {
    this.router.navigate([
      `/${this.selectedCountry?.code}/passport/authorizations`,
    ]);
  }

  // Open important document preview (same logic as DocumentsComponent.onViewDocument)
  onViewImportantDocument(doc: any): void {
    console.log(
      'PassportDashboardComponent: Visualisation du document important:',
      doc
    );

    if (!doc?.url) {
      console.error('PassportDashboardComponent: URL du document manquante');
      this.toastService.error('URL du document manquante');
      return;
    }

    this.documentService.getPresignedUrl(doc.url).subscribe({
      next: (response) => {
        console.log(
          'PassportDashboardComponent: URL presignée obtenue:',
          response?.url
        );
        this.modalService.open({
          component: DocumentViewerModalComponent,
          title: doc.filename || 'Visualisation du document',
          width: '800px',
          closable: true,
          data: {
            document: doc,
            documentUrl: response?.url,
          },
        });
      },
      error: (error) => {
        console.error(
          "PassportDashboardComponent: Erreur lors de l'obtention de l'URL presignée:",
          error
        );
        this.toastService.error('Erreur lors du chargement du document');
      },
    });
  }

  navigateToHealthTracking(): void {
    this.router.navigate([
      `/${this.selectedCountry?.code}/passport/health-tracking`,
    ]);
  }

  navigateToPayments(): void {
    this.router.navigate([
      `/${this.selectedCountry?.code}/passport/payments`,
    ]);
  }

  /**
   * Récupère les statistiques utilisateur depuis l'endpoint /user-stats.
   */
  getUserStats(): Observable<UserStats> {
    return this.dashboardService.getUserStats();
  }
}
